import React, { memo, type FC } from 'react';
import type { StyledProps } from '@compiled/react';
import { styled } from '@compiled/react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import { LegacyDOMBridge } from '@confluence/legacy-dom-bridge';
import { expValEquals } from '@confluence/feature-experiments';

import { BannerStateContainer } from './BannerStateContainer.ts';
import { useBannerList, useBannerTotalHeight } from './useBannerState.ts';

type BannerSpacerDivProps = {
	height: number;
};

// only needs to be exported for testing, so do not include in the index.js file
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const BannerSpacerDiv: React.ComponentType<
	BannerSpacerDivProps &
		React.ClassAttributes<HTMLDivElement> &
		React.HTMLAttributes<HTMLDivElement> &
		StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
> = styled.div<BannerSpacerDivProps>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: (props) => `${props.height}px`,
});

const BANNER_SPACER_CONTAINER_SELECTORS = [`.banner-spacer-container`];

const BannerSpacerWithHooks = () => {
	const bannerHeight = useBannerTotalHeight(false);
	const bannerList = useBannerList();

	return bannerHeight > 0 ? (
		<BannerSpacerDiv
			height={bannerHeight}
			data-testid="banner-spacer"
			data-vc={`banner-spacer-${bannerList}`}
		/>
	) : null;
};

type BannerSpacerProps = {
	usePortalSpacer?: boolean;
};

export const BannerSpacer: FC<BannerSpacerProps> = memo(({ usePortalSpacer = false }) => {
	const spacer = expValEquals('confluence_banner_store_migration', 'isEnabled', true) ? (
		<BannerSpacerWithHooks />
	) : (
		<Subscribe to={[BannerStateContainer]}>
			{(bannerState: BannerStateContainer) => {
				const bannerHeight = bannerState.getTotalHeight(false);
				const bannerList = bannerState.getBannerList();
				return bannerHeight > 0 ? (
					<BannerSpacerDiv
						height={bannerHeight}
						data-testid="banner-spacer"
						data-vc={`banner-spacer-${bannerList}`}
					/>
				) : null;
			}}
		</Subscribe>
	);

	return usePortalSpacer ? (
		<LegacyDOMBridge
			selectors={BANNER_SPACER_CONTAINER_SELECTORS}
			attempts={1}
			retryDelay={500}
			portalNode={spacer}
		/>
	) : (
		spacer
	);
});
